import { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Box,
  useTheme,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelIcon from "@mui/icons-material/Cancel";
import { useFormik } from "formik";
import * as Yup from "yup";
import { END_POINTS } from "../../shared";
import axiosInstance from "../../axios/axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import eventEmitter from "../../components/utils/eventEmitter";

interface JiraFormValues {
  apitoken: string;
  url: string;
  email: string;
}

const validationSchema = Yup.object().shape({
  apitoken: Yup.string().required("API Token is required"),
  url: Yup.string().required("URL is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
});

const JiraIntegration = ({
  setIsJiraClicked,
  isJiraIntegrated,
  fetchProjectListrefetch,
}: any) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const organization_id = useSelector(
    (state: any) => state.UserLoginDetails.organization_id
  );
  const [isFetchDetails, setIsFetchDetails] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(
    isJiraIntegrated ? true : false
  );

  const [isProjectList, setIsProjectList] = useState<
    { id: string; name: string; key: string }[]
  >([]);
  const [selectedProjectKey, setSelectedProjectKey] = useState(
    isJiraIntegrated?.project_type_key || ""
  );
  const [selectedProjectName, setSelectedProjectName] = useState(
    isJiraIntegrated?.project || ""
  );

  const [isProjectStatus, setIsProjectStatus] = useState<
    { id: string; name: string }[]
  >([]);

  const [selectedStatus, setSelectedStatus] = useState(
    isJiraIntegrated?.type || ""
  );
  const [selectedStatusId, setSelectedStatusId] = useState("");

  const [assignees, setAssignees] = useState<
    { display_name: string; account_id: string }[]
  >([]);
  const [selectedAssignee, setSlectedAssignee] = useState(
    isJiraIntegrated?.assignee_name || ""
  );
  const [selectedAccountId, setSelectedAccountId] = useState(
    isJiraIntegrated?.assignee || ""
  );

  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [isLoadingAssignees, setIsLoadingAssignees] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteloading, setDeleteloading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isIntegrationCreated, setIsIntegrationCreated] = useState(
    Boolean(isJiraIntegrated?.id)
  );
  const [shouldAutoFill, setShouldAutoFill] = useState(
    Boolean(isJiraIntegrated?.id)
  );

  console.log(selectedAccountId, "selectedAccountId"); //Assigne id
  console.log(isFetchDetails, "isFetchDetails"); //id
  console.log(selectedProjectName, "selectedProjectName"); //project
  console.log(selectedProjectKey, "selectedProjectKey"); //project key
  console.log(selectedStatus, "selectedStatus");
  const [isValidDetails, setIsValidDetails] = useState(false);

  console.log(organization_id, "organization_id");

  const formik = useFormik<JiraFormValues>({
    initialValues: {
      apitoken: isJiraIntegrated?.api_token || "",
      url: isJiraIntegrated?.server || "",
      email: isJiraIntegrated?.email_id || "",
    },
    validationSchema,
    onSubmit: async () => { },
  });

  // const showDeleteButton =
  //   Boolean(
  //     isJiraIntegrated?.api_token &&
  //     isJiraIntegrated?.server &&
  //     isJiraIntegrated?.email_id
  //   );

  const showDeleteButton = Boolean(
    isIntegrationCreated || isFetchDetails
  );


  // Fetch project list when component mounts
  useEffect(() => {
    fetchProjectList();
  }, [organization_id]);

  const fetchProjectList = async () => {
    setIsLoadingProjects(true);
    try {
      const url = `jira-events/project-list?org_id=${organization_id}`;
      const response = await axiosInstance.get(url);
      setIsProjectList(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching project list:", error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Fetch project statuses when project key is available
  useEffect(() => {
    const fetchProjectStatuses = async () => {
      if (!selectedProjectKey) return;

      setIsLoadingStatuses(true);
      try {
        const url = `jira-events/get_project_statuses?org_id=${organization_id}&&project_key=${selectedProjectKey}`;
        const response = await axiosInstance.get(url);
        const statusNames = response?.data?.data?.statuses;
        setIsProjectStatus(statusNames || []);
      } catch (error) {
        console.error("Error fetching project statuses:", error);
      } finally {
        setIsLoadingStatuses(false);
      }
    };

    fetchProjectStatuses();
  }, [selectedProjectKey, organization_id]);

  // Set assignee when assignees list is loaded and we have isJiraIntegrated data
  useEffect(() => {
    if (shouldAutoFill && isJiraIntegrated && assignees.length > 0) {
      // Find the assignee by account_id
      const assignee = assignees.find(
        (a) => a.account_id === isJiraIntegrated.assignee
      );
      if (assignee) {
        setSlectedAssignee(assignee.display_name);
        setSelectedAccountId(assignee.account_id);
      }
    }
  }, [isJiraIntegrated, assignees]);

  // Fetch assignees when project key is available
  useEffect(() => {
    const fetchAssignees = async () => {
      if (!selectedProjectKey) return;

      setIsLoadingAssignees(true);
      try {
        const url = `jira-events/get_project_assignable_users?org_id=${organization_id}&&project_key=${selectedProjectKey}`;
        const response = await axiosInstance.get(url);
        const assigneeList = response?.data?.data || [];
        setAssignees(assigneeList);
      } catch (error) {
        console.error("Error fetching assignees:", error);
      } finally {
        setIsLoadingAssignees(false);
      }
    };

    fetchAssignees();
  }, [selectedProjectKey, organization_id]);

  // Set initial project key when project list is loaded and we have isJiraIntegrated data
  useEffect(() => {
    if (shouldAutoFill && isJiraIntegrated && isProjectList.length > 0) {
      const project = isProjectList.find(
        (p) => p.name === isJiraIntegrated.project
      );
      if (project) {
        setSelectedProjectKey(project.key);
        setSelectedProjectName(project.name);
      }
    }
  }, [isJiraIntegrated, isProjectList]);

  const handleChangeProjectName = (e: SelectChangeEvent<string>) => {
    const selectedName = e.target.value;
    setSelectedProjectName(selectedName);

    const selected = isProjectList.find((p) => p.name === selectedName);
    if (selected) {
      setSelectedProjectKey(selected.key);
    }
  };

  const handleChangeStatus = (event: SelectChangeEvent<string>) => {
    const selectedstatus = event.target.value;
    setSelectedStatus(selectedstatus);

    const selected = isProjectStatus.find((p) => p.name === selectedstatus);
    if (selected) {
      setSelectedStatusId(selected.id);
    }
  };

  const AssigneehandleChange = (event: SelectChangeEvent<string>) => {
    const display_name = event.target.value;
    setSlectedAssignee(display_name);

    const selected = assignees.find((p) => p.display_name === display_name);
    if (selected) {
      setSelectedAccountId(selected.account_id);
    }
  };

  const handleFetchDetails = async () => {
    if (isFetchingDetails) return;
    setIsFetchingDetails(true);
    try {
      const req_payload = {
        api_token: formik.values.apitoken,
        email_id: formik.values.email,
        server: formik.values.url,
        org_id: organization_id,
      };
      const endpoint = isIntegrationCreated ? END_POINTS.VALIDATE_JIRA : END_POINTS.Jira_Integration;
      const response = await axiosInstance.post(
        endpoint,
        req_payload
      );

      setIsFetchDetails(response?.data?.data?.id);
      if (response?.data?.status === "201" || response?.data?.status === "200" || response?.status === 200) {
        const createdId = response?.data?.data?.id;

        setIsFetchDetails(createdId); // ✅ save response id
        setIsIntegrationCreated(true);
        setIsValidDetails(true); // ✅ THIS IS THE KEY
        setIsDropdownVisible(true);
        toast.success(response.data.message || "Jira details fetched successfully", {
          toastId: "jira-fetch-success",
        });
        fetchProjectList();
      }
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 400) {
        const apiMessage =
          error?.response?.data?.data?.email_id?.[0] ||
          error?.response?.data?.message ||
          "Invalid Jira credentials";

        toast.error(apiMessage, { toastId: "jira-credentials-error" });
      } else {
        toast.error("Failed to fetch Jira details", { toastId: "jira-fetch-error" });
      }
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const req_payload = {
        assignee: selectedAccountId,
        email_id: formik.values.email,
        id: isFetchDetails || isJiraIntegrated?.id,
        priority: "Highest",
        project: selectedProjectName,
        project_type_key: selectedProjectKey,
        type: selectedStatus,
        api_token: formik.values.apitoken,
      };
      const response = await axiosInstance.put(
        "jira-events/jira_integration_update",
        req_payload
      );

      const response_data = response?.data?.data;

      // setIsJiraIntegrated(response_data)
      setIsFetchDetails(response_data?.id);
      await fetchProjectListrefetch();
      setIsJiraClicked(false);
      toast.success(response?.data?.message || "Jira integration updated successfully", {
        toastId: "jira-update-success",
      });
    } catch (error) {
      console.error("Failed to update Jira integration:", error);
      toast.error("Failed to update Jira integration", {
        toastId: "jira-update-error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteJiraIntegration = async () => {
    setDeleteloading(true);
    try {
      const response = await axiosInstance.delete(
        `jira-events/jira-delete?org_id=${organization_id}`
      );
      const successMessage = response?.data?.message || "Jira Integration deleted successfully";
      toast.success(successMessage);
      setIsIntegrationCreated(false);
      setOpenDeleteDialog(false);
      setIsJiraClicked(false);
      eventEmitter.emit("jiraIntegrationDeleted");
      fetchProjectListrefetch();
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.message || "Failed to delete Jira Integration";
      toast.error(errorMessage);
    } finally {
      setDeleteloading(false);
    }
  };

  const handleCloseAndRefetch = async () => {
    try {
      await fetchProjectListrefetch(); // 🔥 refresh Organization Jira data
    } finally {
      setIsJiraClicked(false);         // 🔥 close drawer
    }
  };

  const isLoading =
    (isLoadingProjects && isLoadingStatuses && isLoadingAssignees) || deleteloading;

  const isDropdownFormValid =
    Boolean(selectedProjectName) &&
    Boolean(selectedStatus) &&
    Boolean(selectedAccountId);

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 30,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 10,
            alignItems: "center",
          }}
        >
          <ArrowBackIcon
            onClick={handleCloseAndRefetch}
            sx={{ cursor: "pointer" }}
          />
          <Typography
            variant="body2"
            sx={{ fontWeight: 800, fontSize: "20px" }}
          >
            Jira Integration
          </Typography>
        </div>
        {showDeleteButton && (
          <Button
            variant="outlined"
            sx={{
              borderColor: "#ff4d4f",
              color: "#ff4d4f",
              textTransform: "none",
              // backgroundColor: "#FB4B50",
              // transition: "opacity 0.2s ease-in-out, background-color 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#FB4B50", // keeps it the same red
              },
            }}
            onClick={() => setOpenDeleteDialog(true)}
          >
            Delete
          </Button>
        )}
        <CancelIcon
          onClick={handleCloseAndRefetch}
          sx={{ cursor: "pointer" }}
        />
      </div>

      <form onSubmit={formik.handleSubmit}>
        <TextField
          id="apitoken"
          label="API Token"
          variant="outlined"
          required
          fullWidth
          name="apitoken"
          sx={{
            mb: 3,
            ...(isValidDetails && {
              cursor: "not-allowed",
              "& .MuiInputBase-root": {
                cursor: "not-allowed",
                backgroundColor: isDarkMode ? "#1e1e1e" : "#f5f5f5",
                color: isDarkMode ? "#bdbdbd" : "#000",
              },
              "& .MuiInputLabel-root": {
                color: isDarkMode ? "#9e9e9e" : "#6b6b6b",
              },
            }),
          }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.apitoken}
          error={formik.touched.apitoken && Boolean(formik.errors.apitoken)}
          helperText={
            formik.touched.apitoken && formik.errors.apitoken?.toString()
          }
          disabled={isValidDetails}
        />

        <TextField
          id="url"
          label="URL"
          variant="outlined"
          required
          fullWidth
          name="url"
          sx={{
            mb: 3,
            ...(isValidDetails && {
              cursor: "not-allowed",
              "& .MuiInputBase-root": {
                cursor: "not-allowed",
                backgroundColor: isDarkMode ? "#1e1e1e" : "#f5f5f5",
                color: isDarkMode ? "#bdbdbd" : "#000",
              },
              "& .MuiInputLabel-root": {
                color: isDarkMode ? "#9e9e9e" : "#6b6b6b",
              },
            }),
          }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.url}
          error={formik.touched.url && Boolean(formik.errors.url)}
          helperText={formik.touched.url && formik.errors.url?.toString()}
          disabled={isValidDetails}
        />

        <TextField
          id="email"
          label="Email ID"
          variant="outlined"
          required
          fullWidth
          name="email"
          sx={{
            mb: 3,
            ...(isValidDetails && {
              cursor: "not-allowed",
              "& .MuiInputBase-root": {
                cursor: "not-allowed",
                backgroundColor: isDarkMode ? "#1e1e1e" : "#f5f5f5",
                color: isDarkMode ? "#bdbdbd" : "#000",
              },
              "& .MuiInputLabel-root": {
                color: isDarkMode ? "#9e9e9e" : "#6b6b6b",
              },
            }),
          }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email?.toString()}
          disabled={isValidDetails}
        />

        <div>
          {isDropdownVisible || isJiraIntegrated ? (
            <div>
              {!isValidDetails && (
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleFetchDetails}
                    disabled={!formik.isValid || isFetchingDetails}
                    startIcon={isFetchingDetails ? <CircularProgress size={16} /> : null}
                  >
                    {isFetchingDetails
                      ? "Fetching..."
                      : isIntegrationCreated
                        ? "Validate Jira"
                        : "Fetch Details"}
                  </Button>
                </Box>
              )}

              {isValidDetails && (
                <>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="project-label">Project</InputLabel>
                    <Select
                      labelId="project-label"
                      value={selectedProjectName}
                      label="Project"
                      onChange={handleChangeProjectName}
                    >
                      {isProjectList.map((project) => (
                        <MenuItem key={project.id} value={project.name}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(selectedProjectName.length >= 1 || isJiraIntegrated) && (
                    <FormControl fullWidth sx={{ mb: 3 }} disabled={!selectedProjectName}>
                      <InputLabel id="status-label">Status</InputLabel>
                      <Select
                        labelId="status-label"
                        value={selectedStatus}
                        label="Status"
                        onChange={handleChangeStatus}
                      >
                        {isProjectStatus.map((status) => (
                          <MenuItem key={status.id} value={status.name}>
                            {status.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {(selectedStatus.length >= 1 || isJiraIntegrated) && (
                    <FormControl fullWidth disabled={!selectedStatus}>
                      <InputLabel id="assignee-label">Assignee</InputLabel>
                      <Select
                        labelId="assignee-label"
                        value={selectedAssignee}
                        label="Assignee"
                        onChange={AssigneehandleChange}
                        MenuProps={{
                          PaperProps: { style: { maxHeight: 200 } }
                        }}
                      >
                        {assignees.map((assignee) => (
                          <MenuItem
                            key={assignee.account_id}
                            value={assignee.display_name}
                          >
                            {assignee.display_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}

              <div
                style={{
                  position: "fixed",
                  bottom: 20,
                  right: 20,
                  display: "flex",
                  flexDirection: "row",
                  gap: 8,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setIsJiraClicked(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!isDropdownFormValid || isLoading || isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleFetchDetails}
                disabled={!formik.isValid || !formik.dirty || isFetchingDetails}
                startIcon={
                  isFetchingDetails ? <CircularProgress size={16} /> : null
                }
              >
                {isFetchingDetails ? "Fetching..." : "Fetch Details"}
              </Button>
            </div>
          )}
        </div>
      </form>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        sx={{
          "& .MuiDialog-paper": {
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "400px",
            borderRadius: "4px",
            padding: "10px 0px 24px",
            transform: "translate(-50%, -50%)",
            boxSizing: "border-box",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: 16,
            textAlign: "center",
            padding: "16px 24px 4px",
            position: "relative",
          }}
        >
          Do you want to delete Jira Integration?
          <IconButton onClick={() => setOpenDeleteDialog(false)}
            sx={{
              position: "absolute",
              bottom: 12,
              right: 0,
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          paddingTop: 0,        // 👈 remove default top padding
          paddingBottom: "12px",
        }}>
          <Typography sx={{
            fontWeight: 400,
            fontSize: 13,
            marginTop: 0,
          }}
            color="text.secondary" >
            You cannot undo this action.
          </Typography>
        </DialogContent>

        <DialogActions sx={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          paddingBottom: "24px",
        }}>
          <Button
            variant="outlined"
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              width: 130,
              borderColor: isDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
              color: isDarkMode ? "#fff" : "#000",
              "&:hover": {
                borderColor: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleDeleteJiraIntegration}
            disabled={deleteloading}
            startIcon={deleteloading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            sx={{
              width: 130,
              textTransform: "none",
              backgroundColor: "#FB4B50",
              color: "#fff",
              transition: "opacity 0.2s ease-in-out, background-color 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#FB4B50", // keeps it the same red
              },
              "&.Mui-disabled": {
                backgroundColor: "#FB4B50",
                color: "#fff",
                opacity: 0.5,
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default JiraIntegration;
